import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '@core/services/auth/auth.service';
import { CustomNotificationService } from '@core/services/utils/custom-notification.service';

/**
 * Functional Angular-20 HTTP-Interceptor (Sprint 5c S5c-13).
 *
 * Verantwortlichkeiten:
 * 1. **Auth-Header**: Hängt `Authorization: Bearer <token>` an, sobald der
 *    User-Signal-State einen Sanctum-Token hat.
 * 2. **401/403-Behandlung** auf protected Routes:
 *    - Local State leeren (`AuthService.clear()` → sessionStorage clean)
 *    - Toast „Sitzung abgelaufen" anzeigen (nur 401, nur wenn User gesetzt
 *      war — sonst überflüssig auf dem Login-Screen)
 *    - Redirect zu `/auth/login`
 * 3. **Whitelist** für alle `/auth/*`- und `/login`/`/logout`-Endpoints —
 *    der Fehler wird normal an den Aufrufer durchgereicht, damit Login-
 *    /Register-/Activate-Screens ihre eigenen 401/422-Fehler rendern
 *    können (sonst entsteht ein Endlos-Redirect-Loop, wenn der Login
 *    selber 401 antwortet).
 *
 * **DI-Hinweis (S5c-13-Fix):** `CustomNotificationService` injiziert
 * `TranslateService`, das wiederum `HttpClient` mit diesem Interceptor
 * benutzt — ein eager `inject()` würde einen Initialisierungs-Cycle
 * triggern, der den Translate-File-Load beim App-Start zerschießt
 * (Symptom: UI rendert nur i18n-Keys statt Übersetzungen). Deshalb
 * holen wir die Notification-Service lazy über den `Injector` erst
 * beim tatsächlichen 401-Event.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const injector = inject(Injector);

  const token = authService.user()?.token;
  const authed = token
    ? req.clone({
        setHeaders: { Authorization: `Bearer ${token}` },
      })
    : req;

  return next(authed).pipe(
    catchError((error: HttpErrorResponse) => {
      const status = error.status;

      if ((status === 401 || status === 403) && !isAuthEndpoint(req)) {
        if (status === 401 && authService.user()) {
          // Lazy: erst hier ziehen, sonst Cycle Translate ↔ Interceptor ↔
          // CustomNotificationService → Translate.
          injector.get(CustomNotificationService).warning('AUTH.SESSION_EXPIRED');
        }
        authService.clear();
        void router.navigate(['/auth/login']);
      }

      return throwError(() => error);
    }),
  );
};

/**
 * Whitelist: `/api/login`, `/api/logout` und alle `/api/auth/*`-
 * Endpoints (register, activate, activate/resend, password/forgot,
 * password/reset). Auf diesen Pfaden darf das Interceptor-401-Handling
 * **nicht** zuschlagen — der Aufrufer rendert den Fehler selbst (z.B.
 * „Falsche Zugangsdaten" im Login). Sprint-5c-Review-Fix L-1: enger
 * Regex-Match statt `String.includes('/auth/')`, damit zukünftige
 * Pfade wie `/api/customers/auth-events` nicht versehentlich gewhitelistet
 * werden.
 *
 * In-App-Signatur (ADR-011): zusätzlich `/api/sign/*` — die öffentliche,
 * token-gesicherte Signierseite antwortet bei ungültigem/abgelaufenem
 * Token mit 404/403. Ein eingeloggter Kunde, der seinen eigenen Mail-Link
 * öffnet, darf davon NICHT ausgeloggt werden; die Seite rendert stattdessen
 * lokal „Link ungültig".
 */
function isAuthEndpoint(req: HttpRequest<unknown>): boolean {
  return /\/api\/(login|logout|auth\/|sign\/)/.test(req.url);
}
